import React, { useEffect, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { DashboardService } from "./service";
import { ErrorToast } from "../../shared/toasters/toasters";
import LoadingSpinner from "../statistics/components/LoadingSpinner";

interface MonthlyData {
  categories: string[];
  series: { name: string; data: number[] }[];
}

const CHARTS = [
  { key: "Companies", label: "monthlyGraphs.companies", color: "#4680ff" },
  { key: "Jobs", label: "monthlyGraphs.jobs", color: "#2ca87f" },
  { key: "Coaches", label: "monthlyGraphs.coaches", color: "#e58a00" },
  {
    key: "PostingCompanies",
    label: "monthlyGraphs.postingCompanies",
    color: "#7c4dff",
  },
];

const MonthlyGraphs: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<MonthlyData | null>(null);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    setData(null);
    DashboardService.getMonthlyGraphs(months).then(setData).catch(ErrorToast);
  }, [months]);

  const baseOptions = (color: string): ApexOptions => ({
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: data?.categories ?? [],
      labels: { rotate: -45, style: { fontSize: "11px" } },
    },
    yaxis: { min: 0, labels: { formatter: (v) => Math.floor(v).toString() } },
    colors: [color],
    grid: { borderColor: "#e0e0e0" },
  });

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{t("monthlyGraphs.title")}</h5>
        <Form.Select
          size="sm"
          style={{ width: "auto" }}
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
        >
          <option value={6}>{t("monthlyGraphs.months6")}</option>
          <option value={12}>{t("monthlyGraphs.months12")}</option>
          <option value={24}>{t("monthlyGraphs.months24")}</option>
        </Form.Select>
      </Card.Header>
      <Card.Body>
        {!data ? (
          <LoadingSpinner />
        ) : (
          <Row className="g-4">
            {CHARTS.map(({ key, label, color }) => {
              const series = data.series.find((s) => s.name === key);
              if (!series) return null;
              const total = series.data.reduce((a, b) => a + b, 0);
              return (
                <Col xs={12} md={6} key={key}>
                  <div className="d-flex justify-content-between mb-2">
                    <h6 className="mb-0">{t(label)}</h6>
                    <span className="text-muted small">
                      {t("monthlyGraphs.total")}: <strong>{total}</strong>
                    </span>
                  </div>
                  <ReactApexChart
                    options={baseOptions(color)}
                    series={[{ name: t(label), data: series.data }]}
                    type="bar"
                    height={260}
                  />
                </Col>
              );
            })}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default MonthlyGraphs;
